/**
 * CommentsPanel - Floating panel displaying all comments for the document
 */
import React, { useEffect, useRef, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faTimes,
  faComments,
  faFilter,
  faSort,
  faFileExport,
} from "@fortawesome/free-solid-svg-icons"
import { useDraggablePanel } from "hooks/core/useDraggablePanel"
import { Comment, CommentStatus } from "types/comments.types"
import CommentItem from "./CommentItem.component"

interface CommentsPanelProps {
  isVisible: boolean
  comments: Comment[]
  activeCommentId: string | null
  onClose: () => void
  onCommentSelect: (commentId: string) => void
  onCommentNavigate: (commentId: string) => void
  onCommentReply: (commentId: string) => void
  onStatusChange: (commentId: string, status: CommentStatus) => void
  onCommentDelete: (commentId: string) => void
  onCommentResolve: (commentId: string) => void
  onCommentUpdate?: (commentId: string, content: string) => void
  onReplyDelete?: (commentId: string, replyId: string) => void
  onExportAuditLog?: () => void
  editorContainerRef: React.RefObject<HTMLDivElement | null>
  /** IDs of comments that are new (for highlighting) */
  newCommentIds?: Set<string>
}

type FilterStatus = "all" | CommentStatus
type SortOption = "newest" | "oldest" | "status"

export const CommentsPanel: React.FC<CommentsPanelProps> = ({
  isVisible,
  comments,
  activeCommentId,
  onClose,
  onCommentSelect,
  onCommentNavigate,
  onCommentReply,
  onStatusChange,
  onCommentDelete,
  onCommentResolve,
  onCommentUpdate,
  onReplyDelete,
  onExportAuditLog,
  editorContainerRef,
  newCommentIds = new Set(),
}) => {
  const panelRef = useRef<HTMLDivElement>(null)
  const { position, isDragging, handleHeaderMouseDown } = useDraggablePanel()
  const [filterStatus, setFilterStatus] = useState<FilterStatus>("all")
  const [sortOption, setSortOption] = useState<SortOption>("newest")
  const [showFilterMenu, setShowFilterMenu] = useState(false)
  const [showSortMenu, setShowSortMenu] = useState(false)

  // Filter comments
  const filteredComments = comments.filter((comment) => {
    if (filterStatus === "all") return true
    return comment.status === filterStatus
  })

  // Sort comments
  const statusOrder: Record<CommentStatus, number> = { open: 0, in_progress: 1, resolved: 2 }
  const sortedComments = [...filteredComments].sort((a, b) => {
    switch (sortOption) {
      case "newest":
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      case "oldest":
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      case "status":
        return statusOrder[a.status] - statusOrder[b.status]
      default:
        return 0
    }
  })

  // Set initial position near the right edge of the editor
  useEffect(() => {
    if (!panelRef.current || !editorContainerRef.current || !isVisible) return
    if (position.x !== 0 || position.y !== 0) return // Already positioned

    const editorRect = editorContainerRef.current.getBoundingClientRect()
    const panel = panelRef.current
    const panelRect = panel.getBoundingClientRect()

    // Position on the right side of the editor
    const initialX = window.innerWidth - panelRect.width - 20
    const initialY = editorRect.top + 20

    panel.style.left = `${initialX}px`
    panel.style.top = `${initialY}px`
  }, [editorContainerRef, position.x, position.y, isVisible])

  // Close panel on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isVisible) {
        onClose()
      }
    }

    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [isVisible, onClose])

  // Scroll to active comment when it changes
  useEffect(() => {
    if (activeCommentId && panelRef.current) {
      const commentElement = panelRef.current.querySelector(
        `[data-comment-id="${activeCommentId}"]`
      )
      if (commentElement) {
        commentElement.scrollIntoView({ behavior: "smooth", block: "nearest" })
      }
    }
  }, [activeCommentId])

  if (!isVisible) return null

  const getFilterLabel = (status: FilterStatus): string => {
    switch (status) {
      case "all":
        return "All"
      case "open":
        return "Open"
      case "in_progress":
        return "In Progress"
      case "resolved":
        return "Resolved"
    }
  }

  const getSortLabel = (sort: SortOption): string => {
    switch (sort) {
      case "newest":
        return "Newest"
      case "oldest":
        return "Oldest"
      case "status":
        return "Status"
    }
  }

  return (
    <div
      ref={panelRef}
      className="bg-white rounded-lg shadow-2xl z-[1000] pointer-events-auto flex flex-col"
      style={{
        position: "fixed",
        left: position.x !== 0 ? `${position.x}px` : undefined,
        top: position.y !== 0 ? `${position.y}px` : undefined,
        width: "380px",
        maxHeight: "calc(100vh - 100px)",
        border: "1px solid #D1D5DB",
        cursor: isDragging ? "grabbing" : undefined,
      }}
    >
      {/* Header */}
      <div
        className="px-4 py-3 rounded-t-lg flex items-center justify-between cursor-grab active:cursor-grabbing flex-shrink-0"
        style={{ backgroundColor: "#36C3ED" }}
        onMouseDown={handleHeaderMouseDown}
      >
        <div className="flex items-center gap-2 text-white">
          <FontAwesomeIcon icon={faComments} />
          <span className="font-semibold">Comments</span>
          <span className="bg-white/20 px-2 py-0.5 rounded-full text-xs">
            {comments.length}
          </span>
        </div>
        <button
          onClick={onClose}
          className="text-white hover:text-gray-200 transition-colors"
          aria-label="Close comments panel"
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </div>

      {/* Filter/Sort Bar */}
      <div className="px-4 py-2 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
        {/* Filter dropdown */}
        <div className="relative flex items-center gap-2">
          <button
            onClick={() => {
              setShowFilterMenu(!showFilterMenu)
              setShowSortMenu(false)
            }}
            className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 px-2 py-1 rounded hover:bg-gray-100"
          >
            <FontAwesomeIcon icon={faFilter} size="xs" />
            <span>{getFilterLabel(filterStatus)}</span>
          </button>
          {showFilterMenu && (
            <div className="absolute left-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 py-1 min-w-[120px]">
              {(["all", "open", "in_progress", "resolved"] as FilterStatus[]).map(
                (status) => (
                  <button
                    key={status}
                    className={`w-full px-3 py-1.5 text-left text-sm hover:bg-gray-100 ${
                      filterStatus === status ? "bg-cyan-50 text-cyan-600" : ""
                    }`}
                    onClick={() => {
                      setFilterStatus(status)
                      setShowFilterMenu(false)
                    }}
                  >
                    {getFilterLabel(status)}
                  </button>
                )
              )}
            </div>
          )}
        </div>

        {/* Sort dropdown and Export */}
        <div className="flex items-center gap-1">
          <div className="relative">
            <button
              onClick={() => {
                setShowSortMenu(!showSortMenu)
                setShowFilterMenu(false)
              }}
              className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 px-2 py-1 rounded hover:bg-gray-100"
            >
              <FontAwesomeIcon icon={faSort} size="xs" />
              <span>{getSortLabel(sortOption)}</span>
            </button>
            {showSortMenu && (
              <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 py-1 min-w-[100px]">
                {(["newest", "oldest", "status"] as SortOption[]).map((sort) => (
                  <button
                    key={sort}
                    className={`w-full px-3 py-1.5 text-left text-sm hover:bg-gray-100 ${
                      sortOption === sort ? "bg-cyan-50 text-cyan-600" : ""
                    }`}
                    onClick={() => {
                      setSortOption(sort)
                      setShowSortMenu(false)
                    }}
                  >
                    {getSortLabel(sort)}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Export button */}
          {onExportAuditLog && (
            <button
              onClick={onExportAuditLog}
              className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 px-2 py-1 rounded hover:bg-gray-100"
              title="Export audit log as CSV"
            >
              <FontAwesomeIcon icon={faFileExport} size="xs" />
            </button>
          )}
        </div>
      </div>

      {/* Comments List */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {sortedComments.length === 0 ? (
          <div className="text-center py-8">
            <FontAwesomeIcon
              icon={faComments}
              className="text-4xl text-gray-300 mb-3"
            />
            <p className="text-gray-500">
              {filterStatus === "all"
                ? "No comments yet"
                : `No ${getFilterLabel(filterStatus).toLowerCase()} comments`}
            </p>
            <p className="text-sm text-gray-400 mt-1">
              Select text and press Ctrl+Alt+M to add a comment
            </p>
          </div>
        ) : (
          sortedComments.map((comment) => (
            <div key={comment.id} data-comment-id={comment.id} className="relative">
              {/* New comment indicator */}
              {newCommentIds.has(comment.id) && (
                <span className="absolute -left-1 top-3 w-2 h-2 bg-red-500 rounded-full" />
              )}
              <CommentItem
                comment={comment}
                isActive={activeCommentId === comment.id}
                onSelect={onCommentSelect}
                onNavigate={onCommentNavigate}
                onReply={onCommentReply}
                onStatusChange={onStatusChange}
                onDelete={onCommentDelete}
                onResolve={onCommentResolve}
                onUpdate={onCommentUpdate}
                onDeleteReply={onReplyDelete}
              />
            </div>
          ))
        )}
      </div>

      {/* Footer */}
      <div className="px-4 py-2 bg-gray-50 rounded-b-lg border-t border-gray-200 flex-shrink-0">
        <div className="text-xs text-gray-500 flex items-center justify-between">
          <span>
            {filteredComments.length !== comments.length &&
              `Showing ${filteredComments.length} of ${comments.length}`}
          </span>
          <span className="font-mono">ESC to close</span>
        </div>
      </div>
    </div>
  )
}

export default CommentsPanel
